import { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  styled,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { TrashSimple } from '@phosphor-icons/react';
import theme from '../../../theme';
import { deleteAgentMemoryById, getAgentMemories } from '../../../utils/apis';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DeleteModal from '../../modals/DeleteModal';
import { useAlert } from '../../../hooks/useAlert';
import MessagePopup from '../../common/MessagePopup';
import AgentMemorySkeleton from './AgentMemorySkeleton';

// Define the styled grid overlay for no rows
const StyledGridOverlay = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%',
}));

function CustomNoRowsOverlay() {
  return (
    <StyledGridOverlay>
      <Box sx={{ mt: 2, color: 'text.secondary' }}>No Data Available</Box>
    </StyledGridOverlay>
  );
}

interface MemoryRow {
  id: string;
  createdOn: string;
  createdAt: number;
  userId: string;
  topic: string;
  memory: string;
}

const formatDate = (date: Date | null): string => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Helper function to format timestamp to readable date
const formatTimestampToReadable = (timestamp: number): string => {
  const date = new Date(timestamp * 1000);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const AgentMemory = ({ agentId }: { agentId: string }) => {
  const [fromDate, setFromDate] = useState<Date | null>(() => {
    const today = new Date();
    const lastMonth = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      today.getDate()
    );
    return lastMonth;
  });
  const [toDate, setToDate] = useState<Date | null>(() => new Date());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [selectedUserId, setSelectedUserId] = useState<string>('All Users');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedMemoryId, setSelectedMemoryId] = useState<string | null>(null);
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['agent-memories', agentId, fromDate, toDate],
    queryFn: () =>
      getAgentMemories({
        agentId: agentId,
        startDate: formatDate(fromDate),
        endDate: formatDate(toDate),
      }),
    enabled: !!agentId,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  const { mutate: deleteMemory, isPending } = useMutation({
    mutationFn: deleteAgentMemoryById,
    onSuccess: () => {
      setOpenDeleteModal(false);
      setSelectedMemoryId(null);
      showAlert('Memory deleted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['agent-memories'] });
    },
    onError: () => {
      showAlert('Failed to delete memory', 'error');
    },
  });

  // Transform API data into the format expected by DataGrid
  const rows: MemoryRow[] =
    data?.data?.data?.map((item: any) => ({
      id: item.id,
      createdOn: formatTimestampToReadable(item.created_at),
      createdAt: item.created_at,
      userId: item.user_id,
      topic: item.memory.topics?.[0] || 'No Topic',
      memory: item.memory.memory,
    })) || [];

  // Extract unique user IDs for the dropdown
  const uniqueUserIds = Array.from(new Set(rows.map(row => row.userId)));

  // Filter rows by selected userId if one is selected
  const filteredRows =
    selectedUserId && selectedUserId !== 'All Users'
      ? rows.filter(row => row.userId === selectedUserId)
      : rows;

  // Sort the filtered rows based on the selected sort order
  const sortedRows = [...filteredRows].sort((a, b) => {
    if (sortOrder === 'desc') {
      // Latest to Oldest (descending)
      return b.createdAt - a.createdAt;
    } else {
      // Oldest to Latest (ascending)
      return a.createdAt - b.createdAt;
    }
  });

  const columns: GridColDef[] = [
    {
      field: 'createdOn',
      headerName: 'Created On',
      width: 180,
      headerClassName: 'data-grid-header',
    },
    {
      field: 'userId',
      headerName: 'User Id',
      width: 180,
      headerClassName: 'data-grid-header',
    },
    {
      field: 'topic',
      headerName: 'Topic',
      width: 180,
      headerClassName: 'data-grid-header',
      renderCell: params => (
        <Chip
          label={params.value}
          sx={{
            width: 'fit-content',
            height: 20,
            borderRadius: '19px',
            gap: '10px',
            padding: '2px 12px',
            backgroundColor: '#EFF1FF',
            color: '#6475E8',
            fontSize: '14px',
            '& .MuiChip-label': {
              padding: 0,
            },
          }}
        />
      ),
    },
    {
      field: 'memory',
      headerName: 'Memory',
      flex: 1,
      headerClassName: 'data-grid-header',
    },
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      headerClassName: 'data-grid-header',
      renderCell: params => (
        <IconButton
          sx={{
            color: '#F44336',
            '&:hover': { backgroundColor: 'rgba(244, 67, 54, 0.04)' },
          }}
          onClick={() => {
            setOpenDeleteModal(true);
            setSelectedMemoryId(params.row.id);
          }}
        >
          <TrashSimple size={16} />
        </IconButton>
      ),
    },
  ];

  const handleSortOrderChange = (event: SelectChangeEvent) => {
    setSortOrder(event.target.value as 'desc' | 'asc');
  };

  const handleUserIdChange = (event: SelectChangeEvent) => {
    setSelectedUserId(event.target.value);
  };

  const handleDelete = () => {
    if (selectedMemoryId) {
      deleteMemory(selectedMemoryId);
    }
  };

  return (
    <Box sx={{ width: '100%', padding: '0px 24px 24px', mt: 2 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 2,
          padding: '15px 0',
        }}
      >
        <Typography
          variant="h3"
          sx={{ fontSize: '16px', fontWeight: '500', color: 'custom.textBlue' }}
        >
          Agent Memory
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ width: 170 }}>
            <InputLabel>Sort By Date</InputLabel>
            <Select
              value={sortOrder}
              onChange={handleSortOrderChange}
              label="Sort By Date"
              sx={{
                '& .MuiSelect-select': {
                  fontSize: '14px',
                  color: theme.palette.lightGray.text,
                },
              }}
            >
              <MenuItem value="desc">Latest to Oldest</MenuItem>
              <MenuItem value="asc">Oldest to Latest</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ width: 170 }}>
            <InputLabel>User ID</InputLabel>
            <Select
              value={selectedUserId || ''}
              onChange={handleUserIdChange}
              label="All Users"
              sx={{
                '& .MuiSelect-select': {
                  fontSize: '14px',
                  color: theme.palette.lightGray.text,
                },
              }}
            >
              <MenuItem value="All Users">All Users</MenuItem>
              {uniqueUserIds.map(userId => (
                <MenuItem key={userId} value={userId}>
                  {userId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="From Date"
                value={fromDate}
                views={['year', 'month', 'day']}
                onChange={newValue => setFromDate(newValue)}
                format="dd-MM-yyyy"
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      '& input': {
                        fontSize: '14px',
                        color: theme.palette.lightGray.text,
                        borderRadius: '6px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                  },
                }}
              />

              <DatePicker
                label="To Date"
                value={toDate}
                views={['year', 'month', 'day']}
                onChange={newValue => setToDate(newValue)}
                format="dd-MM-yyyy"
                minDate={fromDate || undefined}
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: {
                      width: 150,
                      '& input': {
                        fontSize: '14px',
                        color: theme.palette.lightGray.text,
                        borderRadius: '6px',
                      },
                      '& .MuiSvgIcon-root': {
                        fontSize: '20px',
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Box>
      </Box>

      {isLoading ? (
        <AgentMemorySkeleton />
      ) : (
        <DataGrid
          rows={sortedRows}
          columns={columns}
          pagination
          initialState={{
            pagination: {
              paginationModel: {
                pageSize: rowsPerPage,
                page: page,
              },
            },
          }}
          pageSizeOptions={[25, 50, 100]}
          rowCount={data?.data?.count || 0}
          onPaginationModelChange={model => {
            setPage(model.page);
            setRowsPerPage(model.pageSize);
          }}
          getRowHeight={() => 60}
          paginationMode="client"
          slots={{
            noRowsOverlay: CustomNoRowsOverlay,
          }}
          loading={false}
          sx={{
            border: `1px solid ${theme.palette.lightGray?.main || '#DCE0E7'}`,
            borderRadius: '12px',
            '--DataGrid-overlayHeight': '300px',
            height: '100%',
            maxHeight: 'calc(100vh - 300px)',
            overflow: 'auto !important',
            '& .MuiDataGrid-main': {
              overflow: 'auto',
            },
            '& .MuiDataGrid-virtualScroller': {
              overflow: 'auto !important',
            },
            '& .data-grid-header': {
              bgcolor: '#F8F9FC',
              color: 'custom.textBlue',
              fontSize: '14px',
              fontWeight: 600,
              padding: '12px 16px',
            },
            '& .MuiDataGrid-cell': {
              padding: '12px 16px',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              '&:focus, &:focus-within': {
                outline: 'none !important',
              },
            },
            '& .MuiDataGrid-row:focus, & .MuiDataGrid-cell:focus': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none !important',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: `1px solid ${theme.palette.lightGray?.main || '#DCE0E7'}`,
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: `1px solid ${theme.palette.lightGray?.main || '#DCE0E7'}`,
            },
          }}
        />
      )}
      <DeleteModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        onDelete={handleDelete}
        isPending={isPending}
        title="Delete Memory"
        description="Are you sure you want to delete this memory?"
      />
      <MessagePopup alert={alert} setAlert={setAlert} />
    </Box>
  );
};

export default AgentMemory;
